import React, { useState } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import { DesktopLayout, StandingsSidebar, LiveAnswer as SharedLiveAnswer, TimerSection } from '../shared';
import { WouldYouRatherResultScreen } from './result-screens/WouldYouRatherResultScreen';
import type { GameStatistics } from '../../types/game';
import { usePartyHostGameLifecycle } from '../../hooks/usePartyHostGameLifecycle';
import {
  PartyHostGameEnd,
  PartyHostPlayingChrome,
  PartyHostResultFrame,
  PartyHostWaiting,
} from './PartyHostShell';
import { wouldYouRatherHostTheme } from '../../themes/cssTheme';
import '../../App.css';
import '../../styles/game-layout.css';

interface WouldYouRatherQuestion {
  questionId?: string;
  id?: string;
  question: string;
  optionA: string;
  optionB: string;
  options: string[]; // [optionA, optionB]
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  isWouldYouRather?: boolean;
}

interface DrinkerEntry {
  playerId: string;
  playerName: string;
  avatar?: string;
  vote: 'AGREE' | 'DISAGREE';
}

interface QuestionResult {
  questionId: string;
  optionA?: string;
  optionB?: string;
  optionACount?: number;
  optionBCount?: number;
  minorityChoice?: string | 'tie';
  debatePrompt?: string;
  drinkers?: DrinkerEntry[];
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  isWouldYouRather?: boolean;
}

interface WouldYouRatherHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: WouldYouRatherQuestion;
    finalLeaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    gameStatistics?: GameStatistics | null;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
}

const THEME = wouldYouRatherHostTheme;

const WouldYouRatherHostDisplay: React.FC<WouldYouRatherHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  hostIsPlaying = false,
}) => {
  const [selectedVote, setSelectedVote] = useState<string | null>(null);

  const {
    currentQuestion,
    timeRemaining,
    questionResult,
    gameEnded,
    waitingForNewGame,
    finalLeaderboard,
    gameStatistics,
    liveAnswers,
    answerSubmitted: voteSubmitted,
    gameEndData,
    submitAnswer,
    restartGame: handleRestartGame,
    playSameGame,
  } = usePartyHostGameLifecycle<WouldYouRatherQuestion, QuestionResult>({
    roomState,
    socket,
    hostIsPlaying,
    isQuestionForGame: (q) => !!q.isWouldYouRather,
    isResultForGame: (r) => !!r.isWouldYouRather,
    isHostAnswerAccepted: (data) => !!data.voteRecorded,
    onResetHostPlaying: () => setSelectedVote(null),
  });

  const handleVoteClick = (vote: string) => {
    if (voteSubmitted || !currentQuestion) return;
    setSelectedVote(vote);
    if (!submitAnswer(vote)) {
      setSelectedVote(null);
    }
  };

  // Convert liveAnswers to shared format
  const sharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map(la => ({
    playerId: la.playerId,
    playerName: la.playerName,
    avatar: la.avatar,
    timestamp: la.timestamp,
    answer: la.answer,
  }));

  if (gameEnded) {
    return (
      <PartyHostGameEnd
        roomName={roomState.roomName}
        gameType={roomState.gameType}
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameStatistics}
        gameEndData={gameEndData}
        onPlaySameGame={playSameGame}
        onBackToGameSelection={onBackToGameSelection}
      />
    );
  }

  if (waitingForNewGame || (!currentQuestion && !questionResult)) {
    return (
      <PartyHostWaiting
        roomName={roomState.roomName}
        emoji="🤔"
        onBackToGameSelection={onBackToGameSelection}
      />
    );
  }

  if (questionResult) {
    return (
      <PartyHostResultFrame roomName={roomState.roomName} onBackToGameSelection={onBackToGameSelection}>
        <WouldYouRatherResultScreen
          questionResult={questionResult}
          roomState={roomState}
          liveAnswers={sharedLiveAnswers}
          onBackToGameSelection={onBackToGameSelection}
          theme={THEME}
        />
      </PartyHostResultFrame>
    );
  }

  const progress = currentQuestion ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);

  return (
    <PartyHostPlayingChrome
      gameLabel="🤔 Would You Rather"
      roomName={roomState.roomName}
      theme={THEME}
      onBackToGameSelection={onBackToGameSelection}
      onRestart={handleRestartGame}
    >
      <div style={{
        minHeight: 'calc(100vh - 82px)',
        width: '100%',
        padding: '1rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
      }}>
        <DesktopLayout
          leftContent={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div
          className="container game-display-container"
          style={{
            paddingTop: '1rem',
            paddingBottom: '1rem',
            marginTop: '1rem',
            marginBottom: 0,
          }}
        >
          <div className="timer-section" style={{ width: '100%', marginBottom: '1.2rem' }}>
            <TimerSection
              questionNumber={currentQuestion?.questionNumber}
              totalQuestions={currentQuestion?.totalQuestions}
              timeSeconds={secondsRemaining}
              progress={progress}
              primaryColor={THEME.primary}
            />
          </div>

          <h1 className="game-question-text" style={{
            color: THEME.primary,
            marginBottom: '1.5rem',
            letterSpacing: '0.03em',
            textShadow: `0 2px 8px ${THEME.primaryBorder}`,
          }}>
            {currentQuestion?.question}
          </h1>

          {hostIsPlaying && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
              marginTop: '1rem',
            }}>
              <button
                onClick={() => currentQuestion && handleVoteClick(currentQuestion.optionA)}
                disabled={voteSubmitted}
                style={{
                  padding: '1.5rem',
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  cursor: voteSubmitted ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                  border: selectedVote === currentQuestion?.optionA ? `3px solid ${THEME.optionA}` : '2px solid rgba(255,255,255,0.2)',
                  background: selectedVote === currentQuestion?.optionA ? THEME.optionALight : 'rgba(255,255,255,0.05)',
                  color: selectedVote === currentQuestion?.optionA ? THEME.optionA : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '2rem' }}>A</span>
                {currentQuestion?.optionA}
              </button>
              <button
                onClick={() => currentQuestion && handleVoteClick(currentQuestion.optionB)}
                disabled={voteSubmitted}
                style={{
                  padding: '1.5rem',
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  cursor: voteSubmitted ? 'default' : 'pointer',
                  transition: 'all 0.2s',
                  border: selectedVote === currentQuestion?.optionB ? `3px solid ${THEME.optionB}` : '2px solid rgba(255,255,255,0.2)',
                  background: selectedVote === currentQuestion?.optionB ? THEME.optionBLight : 'rgba(255,255,255,0.05)',
                  color: selectedVote === currentQuestion?.optionB ? THEME.optionB : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span style={{ fontSize: '2rem' }}>B</span>
                {currentQuestion?.optionB}
              </button>
            </div>
          )}
        </div>
            </div>
          }
          rightContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              theme={THEME}
            />
          }
          mobileOnlyContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={false}
              isMobile={true}
              theme={THEME}
            />
          }
          rightWidth="320px"
        />
      </div>
    </PartyHostPlayingChrome>
  );
};

export default WouldYouRatherHostDisplay;
